<div
	mat-dialog-title
	[align]="'end'">
	<button
		mat-icon-button
		mat-dialog-close>
		<mat-icon>close</mat-icon>
	</button>
</div>
<div mat-dialog-content>
	<div class="flex flex-col gap-y-4">
		<div class="flex items-start gap-5">
			<div
				class="h-16 w-16 bg-primary flex items-center justify-center rounded-2xl shadow-lg shadow-primary/20 shrink-0">
				<mat-icon class="text-white !text-3xl !w-[30px] !h-[30px]">medication</mat-icon>
			</div>
			<div class="flex-1">
				<h1 class="text-3xl font-extrabold tracking-tight text-on-surface mb-1 uppercase">
					{{ drugInstitute.drug.label }}
				</h1>
				<p class="text-sm font-semibold text-primary uppercase tracking-wider mb-3">
					{{ drugInstitute.drug.laboratory.label }}
				</p>
				<div class="flex flex-wrap gap-3">
					@if (drugInstitute.risk) {
						<img
							[matTooltip]="'drugs.drug.risk' | translate"
							src="assets/images/alert.png"
							alt="risk" />
					}
					<span
						class="inline-flex items-center gap-1.5 px-3 py-1 bg-secondary-container text-on-secondary-container text-xs font-bold rounded-full">
						<mat-icon class="text-sm">science</mat-icon>
						DCI : {{ drugInstitute.drug.dci }}
					</span>
					<span
						class="inline-flex items-center gap-1.5 px-3 py-1 bg-surface-container text-on-surface-variant text-xs font-bold rounded-full">
						<mat-icon class="text-sm">label</mat-icon>
						ATC : {{ drugInstitute.drug.atc }}
					</span>
					<span
						class="inline-flex items-center gap-1.5 px-3 py-1 bg-surface-container text-on-surface-variant text-xs font-bold rounded-full">
						<mat-icon class="text-sm">pin</mat-icon>
						UCD : {{ drugInstitute.drug.identifiers["ucd"] }}
					</span>
				</div>
			</div>
		</div>
		<div class="flex-1 overflow-y-auto p-8 space-y-10">
			<!-- Indications -->
			<section>
				<div class="flex items-center gap-2 mb-4">
					<mat-icon class="text-primary text-xl">checklist</mat-icon>
					<div class="text-xl font-bold text-on-surface">
						{{ "drugs.drug.indications.label" | translate }}
					</div>
				</div>
				<div class="p-6 bg-surface-container-low rounded-xl border-l-4 border-primary">
					<div
						#indicationsContent
						class="expandable-content content-paragraph text-on-surface text-sm"
						[class.expanded]="expanded.indications">
						<p
							#indicationsInner
							class="mb-4"
							[innerHTML]="drugDetails.indications"></p>
					</div>
					@if (expanded.indications || hasMore.indications) {
						<div
							class="mt-3 text-sm font-bold text-primary flex items-center gap-1 hover:underline cursor-pointer select-none"
							(click)="toggle('indications')">
							{{ expanded.indications ? "Voir moins" : "Voir plus" }}
							<mat-icon
								class="text-base transition-transform duration-300"
								[class.rotate-180]="expanded.indications">
								expand_more
							</mat-icon>
						</div>
					}
				</div>
			</section>
			<!-- Contre-indications -->
			<section>
				<div class="flex items-center gap-2 mb-4">
					<mat-icon class="text-error text-xl">warning</mat-icon>
					<div class="text-xl font-bold text-on-surface">
						{{ "drugs.drug.contraindications.label" | translate }}
					</div>
				</div>
				<div class="p-6 bg-error-container/10 rounded-xl border-l-4 border-error">
					<div
						#contraindicationsContent
						class="expandable-content content-paragraph text-on-surface text-sm"
						[class.expanded]="expanded.contraindications">
						<p #contraindicationsInner>
							@for (item of contraindications; track item) {
								<li>{{ item }}</li>
							}
						</p>
					</div>
					@if (expanded.contraindications || hasMore.contraindications) {
						<div
							class="mt-3 text-sm font-bold text-error flex items-center gap-1 hover:underline cursor-pointer select-none"
							(click)="toggle('contraindications')">
							{{ expanded.contraindications ? "Voir moins" : "Voir plus" }}
							<mat-icon
								class="material-symbols-outlined text-base transition-transform duration-300"
								[class.rotate-180]="expanded.contraindications">
								expand_more
							</mat-icon>
						</div>
					}
				</div>
			</section>
			<!-- Posologies -->
			<section>
				<div class="flex items-center gap-2 mb-4">
					<mat-icon class="text-primary text-xl">straighten</mat-icon>
					<div class="text-xl font-bold text-on-surface">{{ "drugs.drug.posologies.label" | translate }}</div>
				</div>
				<div class="space-y-6">
					<div class="bg-white p-6 rounded-xl shadow-sm border border-outline-variant/10">
						<div
							#posologiesContent
							class="expandable-content content-paragraph text-on-surface text-sm"
							[class.expanded]="expanded.posologies">
							<p
								#posologiesInner
								class="mb-4"
								[innerHTML]="drugDetails.posologies"></p>
						</div>
						@if (expanded.posologies || hasMore.posologies) {
							<div
								class="mt-2 text-sm font-bold text-primary flex items-center gap-1 hover:underline cursor-pointer select-none"
								(click)="toggle('posologies')">
								{{ expanded.posologies ? "Voir moins" : "Voir plus" }}
								<mat-icon
									class="text-base transition-transform duration-300"
									[class.rotate-180]="expanded.posologies">
									expand_more
								</mat-icon>
							</div>
						}
					</div>
				</div>
			</section>
			<!-- Alternatives & Visuals -->
			@if (drugInstitute.alternatives?.length > 0) {
				<div class="flex items-center gap-2 mb-4">
					<mat-icon class="text-secondary text-xl">swap_horiz</mat-icon>
					<div class="text-lg font-bold text-on-surface">
						{{ "drugs.drug.alternatives.label" | translate }}
					</div>
				</div>
				<div class="flex flex-col gap-2">
					@for (alternative of drugInstitute.alternatives; track alternative.id) {
						<div
							class="px-4 py-3 bg-secondary-container/50 border border-secondary-container text-on-secondary-container rounded-lg font-semibold text-sm flex items-center justify-between">
							<span>{{ alternative.drug.label }}</span>
							<mat-icon class="text-sm opacity-50">arrow_forward</mat-icon>
						</div>
					}
				</div>
			}
		</div>
	</div>
</div>
